// YERİ: src/app/domains/site/sections/sportsclub.sections.ts
//
// SPOR KULÜBÜ BÖLÜMLERİ — fikstür, puan durumu, saha planı.
//
// ── NEDEN AYARDAN DEĞİL SUNUCUDAN ─────────────────────────────────────────
// Bu üçü kulübün portalda ZATEN tuttuğu kayıtlar. Bölüm ayarına elle
// yazdırılsaydı kulüp her maçtan sonra aynı veriyi İKİ yerde güncellemek
// zorunda kalırdı ve site kaçınılmaz olarak eskirdi. Bölüm yalnız "neyi
// göstereyim" (kapsam, adet, başlık) sorusunu ayardan okur.
//
// ── VERİ YOKSA BÖLÜM BASILMAZ ─────────────────────────────────────────────
// Örnek maç / uydurma takım ADI GÖSTERİLMEZ. Blog bölümünde tam olarak bu
// hata vardı: liste boşken "Yazı Başlığı 1" basılıyor, ziyaretçi var olmayan
// içeriğe tıklıyordu. Fikstürde karşılığı "Takım A - Takım B" olurdu.

import { Component, OnInit, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { SiteConfig, cfg } from './section.model';
import {
  BoardMarker, DEFAULT_BOARD_STYLE, DrawPhase, SHIRT, boardSvg, boardVertical, kitFromColors,
  normalizePhases, normalizeStyle, resolvePitchKind, slotAbbr,
} from '@/app/shared/sports/pitch-render';
import { SiteChromeText } from '../core/site-chrome-text';
import {
  TenantWebService, TenantFixture, TenantFormation, TenantStandings,
} from '../core/tenant-web.service';

/**
 * Sunucudan gelen tarih ZAMAN DİLİMSİZ (naive) bir duvar saatidir: kulüp
 * "19:00" girdiyse ziyaretçi de 19:00 görmeli — ziyaretçinin tarayıcı
 * saat dilimine göre kaydırmak maç saatini yanlış gösterir.
 *
 * Bu yüzden değer UTC'ymiş gibi ayrıştırılıp `timeZone:'UTC'` ile basılıyor:
 * sonuç, hangi ülkeden bakılırsa bakılsın girilen saatin aynısı.
 */
function duvarSaati(iso: string | null | undefined, lang: string, saatliMi: boolean): string {
  if (!iso) return '';

  // Sondaki 'Z' veya '+03:00' gibi ekler atılır; kalan kısım UTC sayılır.
  const temiz = iso.replace(/(Z|[+-]\d{2}:?\d{2})$/i, '');
  const d = new Date(temiz + 'Z');
  if (Number.isNaN(d.getTime())) return '';

  const option: Intl.DateTimeFormatOptions = saatliMi
    ? { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'UTC' }
    : { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' };

  try {
    return new Intl.DateTimeFormat(lang, option).format(d);
  } catch {
    // Geçersiz dil kodu Intl'i patlatır; site bir tarih yüzünden çökmemeli.
    return new Intl.DateTimeFormat('en', option).format(d);
  }
}

// ══════════════════════════════════════════════════════════════════════════
// FİKSTÜR / SONUÇLAR
// ══════════════════════════════════════════════════════════════════════════

@Component({
  selector: 'sec-fixture-list',
  standalone: true,
  template: `
    @if (mac().length) {
    <section id="fixture" class="bg-white py-20 dark:bg-neutral-950">
      <div class="mx-auto max-w-5xl px-6 lg:px-8">
        <div class="text-center">
          @if (eyebrow()) {
            <p class="text-sm font-semibold uppercase tracking-widest" style="color:var(--site-accent)">{{ eyebrow() }}</p>
          }
          <h2 class="mt-3 font-[family-name:var(--site-head-font)] text-3xl font-bold tracking-tight text-neutral-900 dark:text-white sm:text-4xl">{{ title() }}</h2>
          @if (subtitle()) {
            <p class="mx-auto mt-4 max-w-2xl text-lg text-neutral-600 dark:text-neutral-400">{{ subtitle() }}</p>
          }
        </div>

        <ul class="mt-10 space-y-3">
          @for (m of mac(); track m.id) {
            <li class="rounded-2xl border border-neutral-200 bg-white p-4 transition hover:shadow-md dark:border-neutral-800 dark:bg-neutral-900 sm:p-5">
              <!-- Üst satır: turnuva + hafta + tarih -->
              <div class="flex flex-wrap items-center justify-between gap-2 text-xs text-neutral-500 dark:text-neutral-400">
                <span class="font-medium uppercase tracking-wide">
                  {{ m.competitionName }}
                  @if (m.round > 0) { <span class="ml-1">· {{ text.t('sp.round') }} {{ m.round }}</span> }
                </span>
                @if (m.scheduledAt) { <span>{{ date(m) }}</span> }
              </div>

              <!-- Eşleşme: ev sahibi — skor/vs — deplasman -->
              <div class="mt-3 grid grid-cols-[1fr_auto_1fr] items-center gap-3">
                <span class="text-right text-base font-semibold text-neutral-900 dark:text-white sm:text-lg">{{ m.homeTeamName }}</span>

                @if (skorVar(m)) {
                  <span class="rounded-lg px-3 py-1 text-base font-bold tabular-nums text-white sm:text-lg"
                        style="background:var(--site-primary)">{{ m.scoreHome }} - {{ m.scoreAway }}</span>
                } @else {
                  <span class="rounded-lg border border-neutral-200 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:border-neutral-700 dark:text-neutral-400">{{ text.t('sp.vs') }}</span>
                }

                <span class="text-left text-base font-semibold text-neutral-900 dark:text-white sm:text-lg">{{ m.awayTeamName }}</span>
              </div>

              <!-- Alt satır: saha + ertelendi rozeti -->
              @if (m.venue || ertelendi(m)) {
                <div class="mt-3 flex flex-wrap items-center justify-center gap-2 text-xs text-neutral-500 dark:text-neutral-400">
                  @if (m.venue) { <span>{{ text.t('sp.venue') }}: {{ m.venue }}</span> }
                  @if (ertelendi(m)) {
                    <span class="rounded-full bg-amber-100 px-2 py-0.5 font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">{{ text.t('sp.postponed') }}</span>
                  }
                </div>
              }
            </li>
          }
        </ul>
      </div>
    </section>
    }
  `,
})
export class FixtureListSection implements OnInit {
  protected text = inject(SiteChromeText);
  private svc = inject(TenantWebService);

  config = input<SiteConfig>({});
  clientId = input<number>(0);

  protected mac = signal<TenantFixture[]>([]);

  ngOnInit() {
    const id = this.clientId();
    if (!id) return;

    const scope = String(cfg(this.config(), 'scope', 'upcoming')) as 'upcoming' | 'results' | 'all';
    const qty = Number(cfg(this.config(), 'count', 5)) || 5;

    this.svc.getSportsFixtures(id, scope, qty).subscribe({
      next: (l) => this.mac.set(l),
      // Uç ulaşılamazsa bölüm sessizce boş kalır; ziyaretçiye hata basmak yerine
      // bölümü hiç göstermemek doğru.
      error: () => this.mac.set([]),
    });
  }

  protected eyebrow = computed(() => cfg(this.config(), 'eyebrow', ''));
  protected title = computed(() => cfg(this.config(), 'title', this.text.t('fb.fikstur')));
  protected subtitle = computed(() => cfg(this.config(), 'subtitle', ''));

  /**
   * Saat gösterilsin mi — kulüp yalnız günü duyurmak isteyebilir.
   *
   * Değer select'ten geldiği için METİN ('true'/'false'); ham boolean olarak
   * karşılaştırılsaydı 'false' dizesi doğru sayılır ve ayar hiç işlemezdi.
   */
  private saatli = computed(() => String(cfg(this.config(), 'showTime', 'true')) !== 'false');

  protected date = (m: TenantFixture) => duvarSaati(m.scheduledAt, this.text.lang(), this.saatli());
  protected skorVar = (m: TenantFixture) => m.scoreHome != null && m.scoreAway != null;
  protected ertelendi = (m: TenantFixture) => (m.status || '').toLowerCase() === 'postponed';
}

// ══════════════════════════════════════════════════════════════════════════
// PUAN DURUMU
// ══════════════════════════════════════════════════════════════════════════

@Component({
  selector: 'sec-standings-table',
  standalone: true,
  template: `
    @if (row().length) {
    <section id="standings" class="bg-neutral-50 py-20 dark:bg-neutral-900">
      <div class="mx-auto max-w-4xl px-6 lg:px-8">
        <div class="text-center">
          @if (eyebrow()) {
            <p class="text-sm font-semibold uppercase tracking-widest" style="color:var(--site-accent)">{{ eyebrow() }}</p>
          }
          <h2 class="mt-3 font-[family-name:var(--site-head-font)] text-3xl font-bold tracking-tight text-neutral-900 dark:text-white sm:text-4xl">{{ title() }}</h2>
          <p class="mt-3 text-sm text-neutral-500 dark:text-neutral-400">
            {{ tablo()?.competitionName }}
            @if (tablo()?.season) { <span>· {{ tablo()?.season }}</span> }
          </p>
        </div>

        <!-- Dar ekranda tablo kendi içinde kayar; sayfa yatay kaymaz. -->
        <div class="mt-8 overflow-x-auto rounded-2xl border border-neutral-200 bg-white dark:border-neutral-800 dark:bg-neutral-950">
          <table class="w-full min-w-[34rem] text-sm">
            <thead>
              <tr class="border-b border-neutral-200 text-xs uppercase tracking-wide text-neutral-500 dark:border-neutral-800 dark:text-neutral-400">
                <th class="px-3 py-3 text-left font-semibold">#</th>
                <th class="px-3 py-3 text-left font-semibold">{{ text.t('sp.team') }}</th>
                <th class="px-2 py-3 text-center font-semibold" [title]="text.t('sp.playedLong')">{{ text.t('sp.played') }}</th>
                <th class="px-2 py-3 text-center font-semibold" [title]="text.t('sp.wonLong')">{{ text.t('sp.won') }}</th>
                <th class="px-2 py-3 text-center font-semibold" [title]="text.t('sp.drawnLong')">{{ text.t('sp.drawn') }}</th>
                <th class="px-2 py-3 text-center font-semibold" [title]="text.t('sp.lostLong')">{{ text.t('sp.lost') }}</th>
                <th class="px-2 py-3 text-center font-semibold" [title]="text.t('sp.goalsLong')">{{ text.t('sp.goals') }}</th>
                <th class="px-2 py-3 text-center font-semibold" [title]="text.t('sp.diffLong')">{{ text.t('sp.diff') }}</th>
                <th class="px-3 py-3 text-center font-bold" [title]="text.t('sp.pointsLong')">{{ text.t('sp.points') }}</th>
              </tr>
            </thead>
            <tbody>
              @for (r of row(); track r.teamName) {
                <tr class="border-b border-neutral-100 last:border-0 dark:border-neutral-900">
                  <td class="px-3 py-3 text-left tabular-nums text-neutral-500 dark:text-neutral-400">{{ r.rank }}</td>
                  <td class="px-3 py-3 text-left font-semibold text-neutral-900 dark:text-white">{{ r.teamName }}</td>
                  <td class="px-2 py-3 text-center tabular-nums text-neutral-600 dark:text-neutral-400">{{ r.played }}</td>
                  <td class="px-2 py-3 text-center tabular-nums text-neutral-600 dark:text-neutral-400">{{ r.won }}</td>
                  <td class="px-2 py-3 text-center tabular-nums text-neutral-600 dark:text-neutral-400">{{ r.drawn }}</td>
                  <td class="px-2 py-3 text-center tabular-nums text-neutral-600 dark:text-neutral-400">{{ r.lost }}</td>
                  <td class="px-2 py-3 text-center tabular-nums text-neutral-600 dark:text-neutral-400">{{ r.goalsFor }}-{{ r.goalsAgainst }}</td>
                  <td class="px-2 py-3 text-center tabular-nums text-neutral-600 dark:text-neutral-400">{{ r.goalDifference > 0 ? '+' : '' }}{{ r.goalDifference }}</td>
                  <td class="px-3 py-3 text-center font-bold tabular-nums" style="color:var(--site-primary)">{{ r.points }}</td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </div>
    </section>
    }
  `,
})
export class StandingsTableSection implements OnInit {
  protected text = inject(SiteChromeText);
  private svc = inject(TenantWebService);

  config = input<SiteConfig>({});
  clientId = input<number>(0);

  protected tablo = signal<TenantStandings | null>(null);
  protected row = computed(() => this.tablo()?.rows ?? []);

  ngOnInit() {
    const id = this.clientId();
    if (!id) return;

    // Turnuva seçilmediyse sunucu en güncel olanı seçer.
    const ham = cfg(this.config(), 'competitionId', '');
    const turnuva = Number(ham) > 0 ? Number(ham) : null;

    this.svc.getSportsStandings(id, turnuva).subscribe({
      next: (t) => this.tablo.set(t),
      error: () => this.tablo.set(null),
    });
  }

  protected eyebrow = computed(() => cfg(this.config(), 'eyebrow', ''));
  protected title = computed(() => cfg(this.config(), 'title', this.text.t('sd.puanDurumu')));
}

// ══════════════════════════════════════════════════════════════════════════
// SAHA PLANI (DİZİLİŞ)
// ══════════════════════════════════════════════════════════════════════════
//
// Tahta, yönetim panelindeki taktik tahtayla AYNI çizim çekirdeğinden
// (shared/sports/pitch-render) çiziliyor: saha türü, takım rengi, forma
// işaretleri, yön ve ad/numara/mevki göstergeleri dizilişle birlikte
// kaydedilen görünümden (StyleJson) gelir. Bölüm ayarı yalnız HANGİ takımın
// HANGİ dizilişlerinin gösterileceğini ve taktik çizimlerinin açık olup
// olmadığını söyler. Çizimler varsayılan KAPALI: antrenörün taktiği kulüp
// açıkça istemeden herkese açılmaz.

@Component({
  selector: 'sec-formation-pitch',
  standalone: true,
  template: `
    @if (plan(); as p) {
    <section id="formation" class="bg-white py-20 dark:bg-neutral-950">
      <div class="mx-auto max-w-5xl px-6 lg:px-8">
        <div class="text-center">
          @if (eyebrow()) {
            <p class="text-sm font-semibold uppercase tracking-widest" style="color:var(--site-accent)">{{ eyebrow() }}</p>
          }
          <h2 class="mt-3 font-[family-name:var(--site-head-font)] text-3xl font-bold tracking-tight text-neutral-900 dark:text-white sm:text-4xl">{{ title() }}</h2>
          @if (subtitle()) {
            <p class="mx-auto mt-4 max-w-2xl text-lg text-neutral-600 dark:text-neutral-400">{{ subtitle() }}</p>
          }
        </div>

        <!-- Birden çok diziliş: sekmeler -->
        @if (planlar().length > 1) {
          <div class="mt-8 flex flex-wrap justify-center gap-2" role="tablist" [attr.aria-label]="text.t('sp.formations')">
            @for (f of planlar(); track f.id; let i = $index) {
              <button type="button" role="tab" [attr.aria-selected]="i === secili()"
                      class="rounded-full border px-4 py-1.5 text-sm font-semibold transition"
                      [class]="i === secili()
                        ? 'border-transparent text-white shadow'
                        : 'border-neutral-200 text-neutral-700 hover:bg-neutral-50 dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-900'"
                      [style.background]="i === secili() ? 'var(--site-primary)' : null"
                      (click)="sec(i)">{{ f.name }}</button>
            }
          </div>
        }

        <div class="mx-auto mt-8 w-full" [style.max-width]="genislik()">
          <p class="mb-3 text-center text-sm text-neutral-500 dark:text-neutral-400">
            {{ p.teamName }}
            @if (p.name) { <span>· {{ p.name }}</span> }
          </p>

          <!-- Tahta: tek SVG, her ekranda aynı oranla ölçeklenir. -->
          <div class="overflow-hidden rounded-2xl shadow-lg ring-1 ring-black/10" [innerHTML]="tahta()"></div>

          <!-- Taktik aşamaları (Hücum / Savunma / Duran top…) -->
          @if (asamalar().length > 1) {
            <div class="mt-4 flex flex-wrap justify-center gap-2" role="tablist" [attr.aria-label]="text.t('sp.tacticPhases')">
              @for (a of asamalar(); track a.id; let i = $index) {
                <button type="button" role="tab" [attr.aria-selected]="i === asama()"
                        class="rounded-lg border px-3 py-1 text-xs font-semibold transition"
                        [class]="i === asama()
                          ? 'border-neutral-900 bg-neutral-900 text-white dark:border-white dark:bg-white dark:text-neutral-900'
                          : 'border-neutral-200 text-neutral-600 hover:bg-neutral-50 dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-900'"
                        (click)="asama.set(i)">{{ a.name || (i + 1) }}</button>
              }
            </div>
          } @else if (asamalar().length === 1 && asamalar()[0].name) {
            <p class="mt-3 text-center text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">{{ asamalar()[0].name }}</p>
          }

          <!-- Yedekler -->
          @if (yedekler().length) {
            <div class="mt-6">
              <h3 class="text-center text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">{{ text.t('sp.bench') }}</h3>
              <ul class="mt-3 flex flex-wrap justify-center gap-2">
                @for (b of yedekler(); track $index) {
                  <li class="flex items-center gap-2 rounded-lg border border-neutral-200 bg-neutral-50 py-1 pl-1.5 pr-3 text-sm text-neutral-800 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-200">
                    <svg viewBox="0 0 40 40" class="h-7 w-7 shrink-0" aria-hidden="true">
                      <path [attr.d]="shirt" [attr.fill]="kit().shirt" stroke="rgba(0,0,0,0.35)" stroke-width="1.2" stroke-linejoin="round" />
                      @if (stil().showNumbers) {
                        <text x="20" y="28" text-anchor="middle" font-size="13" font-weight="800" font-family="Arial, Helvetica, sans-serif" [attr.fill]="kit().text">{{ b.number ?? '' }}</text>
                      }
                    </svg>
                    <span>{{ b.playerName }}</span>
                    @if (b.captain) { <span class="rounded-full bg-amber-400 px-1.5 text-[10px] font-black text-neutral-900">C</span> }
                  </li>
                }
              </ul>
            </div>
          }
        </div>
      </div>
    </section>
    }
  `,
})
export class FormationPitchSection {
  protected text = inject(SiteChromeText);
  private svc = inject(TenantWebService);
  private sanitizer = inject(DomSanitizer);

  config = input<SiteConfig>({});
  clientId = input<number>(0);

  protected readonly shirt = SHIRT;
  protected planlar = signal<TenantFormation[]>([]);
  protected secili = signal(0);
  protected asama = signal(0);

  /** Request key: the editor preview updates [config] in place, so a change refetches. */
  private istek = computed(() => {
    const c = this.config();
    const teamId = Number(cfg(c, 'teamId', 0)) || null;
    const ids = idListesi(cfg<unknown>(c, 'formationIds', []));
    const eski = Number(cfg(c, 'formationId', 0)) || null;
    const cizim = String(cfg(c, 'showDrawings', 'false')) === 'true';
    return { id: this.clientId(), teamId, ids, eski, cizim, key: JSON.stringify([this.clientId(), teamId, ids, eski, cizim]) };
  }, { equal: (a, b) => a.key === b.key });

  constructor() {
    effect(() => {
      const r = this.istek();
      untracked(() => this.yukle(r));
    });
  }

  protected eyebrow = computed(() => cfg(this.config(), 'eyebrow', ''));
  protected title = computed(() => cfg(this.config(), 'title', this.text.t('fb.sahaPlani')));
  protected subtitle = computed(() => cfg(this.config(), 'subtitle', ''));

  protected plan = computed(() => this.planlar()[this.secili()] ?? this.planlar()[0] ?? null);
  protected stil = computed(() => {
    const s = this.plan()?.style;
    return s ? normalizeStyle(s) : DEFAULT_BOARD_STYLE;
  });
  protected kit = computed(() => kitFromColors(this.stil().color, this.stil().gkColor ?? DEFAULT_BOARD_STYLE.gkColor));
  private kind = computed(() => {
    const p = this.plan();
    return resolvePitchKind(p?.sport, this.stil().pitch ?? p?.pitch ?? null, p?.spots.length ?? 0);
  });
  private dikey = computed(() => boardVertical(this.kind(), this.stil().orientation));
  /** Vertical boards stay phone-sized on desktop; horizontal ones use the column. */
  protected genislik = computed(() => this.dikey() ? '30rem' : '56rem');

  protected asamalar = computed<DrawPhase[]>(() => {
    if (!this.istek().cizim) return [];
    const list = normalizePhases(this.plan()?.drawings ?? []).filter(a => a.items.length);
    return String(cfg(this.config(), 'drawingPhase', 'all')) === 'first' ? list.slice(0, 1) : list;
  });

  protected yedekler = computed(() => (this.plan()?.bench ?? []).filter(b => !!b.playerName));

  protected tahta = computed<SafeHtml>(() => {
    const p = this.plan();
    if (!p) return this.sanitizer.bypassSecurityTrustHtml('');
    const st = this.stil();
    const markers: BoardMarker[] = p.spots.map(s => ({
      x: s.x, y: s.y, filled: !!s.playerName, jersey: s.number ?? null, name: s.playerName ?? '',
      pos: slotAbbr(s.slot) || (s.label ?? ''), slotLabel: s.label ?? '',
      captain: !!s.captain, gk: s.slot === 'gk',
    }));
    const items = this.asamalar()[this.asama()]?.items ?? [];
    const svg = boardSvg({
      kind: this.kind(), vertical: this.dikey(), width: this.dikey() ? 960 : 1500,
      markers, kit: this.kit(), show: { names: st.showNames, numbers: st.showNumbers, positions: st.showPositions }, items,
    }, [p.teamName, p.name].filter(Boolean).join(' · '));
    // Every user text inside the SVG is escaped by pitch-render (xmlEsc).
    return this.sanitizer.bypassSecurityTrustHtml(svg);
  });

  protected sec(i: number) {
    this.secili.set(i);
    this.asama.set(0);
  }

  private yukle(r: { id: number; teamId: number | null; ids: number[]; eski: number | null; cizim: boolean }) {
    if (!r.id) { this.planlar.set([]); return; }
    this.secili.set(0);
    this.asama.set(0);
    // Old sections stored a single formationId (typed by hand): keep serving them.
    if (!r.teamId && !r.ids.length && r.eski) {
      this.svc.getSportsFormation(r.id, r.eski, r.cizim).subscribe({
        next: (p) => this.planlar.set(p && p.spots.length ? [p] : []),
        error: () => this.planlar.set([]),
      });
      return;
    }
    this.svc.getSportsFormations(r.id, r.teamId, r.ids, r.cizim).subscribe({
      // A plan without positions has nothing to draw — no empty pitch on the site.
      next: (l) => this.planlar.set(l.filter(p => p.spots.length)),
      error: () => this.planlar.set([]),
    });
  }
}

/** formationIds may be stored as an array or a comma string; keeps order, drops junk. */
function idListesi(v: unknown): number[] {
  const raw = Array.isArray(v) ? v : typeof v === 'string' ? v.split(',') : [];
  const out: number[] = [];
  for (const x of raw) {
    const n = Number(x);
    if (Number.isInteger(n) && n > 0 && !out.includes(n)) out.push(n);
  }
  return out.slice(0, 12);
}
